import { hostTimeZone } from './schedule';
import type {
  Channel,
  GenerationTarget,
  GenerationOperation,
  HostVideoCopy,
  GenerationPlan,
  PostLinkInput,
} from '@str/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AnalyticsDto,
  BookingDto,
  CalendarPlanDto,
  HomeDto,
  HomePeriod,
  PostResultsDto,
  ConnectionDto,
  GenerationDto,
  ImportProgress,
  InviteDto,
  MediaDto,
  NotificationsDto,
  PostDto,
  PropertyDto,
  ProviderAvailability,
  PublisherRun,
  ReferenceDto,
  SubscriptionDto,
  TeamDto,
  SubscriptionSummary,
} from '@str/api/types';
import type {
  MediaSource,
  CreatePostInput,
  LogBookingInput,
  NotificationsInput,
  ProviderKey,
  SchedulePostInput,
  UpdatePropertyInput,
} from '@str/shared';
import { api, unwrap, ApiError } from './api';

export type CreditsDto = {
  balance: number;
  grantCredits: number;
  packCredits: number;
  /** When the next monthly grant lands; null on a trial (it spends its pack). */
  resetsAt: string | null;
};

export type Me = {
  user: {
    id: string;
    name: string;
    email: string;
    emailVerified: boolean;
    image: string | null;
  } | null;
  subscription: SubscriptionSummary | null;
  properties: PropertyDto[];
  /** Posts scheduled or published this month. */
  usage: { used: number } | null;
  credits: CreditsDto | null;
  /** Email preferences; null when signed out. */
  notifications: NotificationsDto | null;
  billing: { mode: 'stripe' | 'mock'; publishableKey: string | null };
  /** PostHog's public project settings; null = analytics off on this deployment. */
  analytics: { posthogKey: string; posthogHost: string } | null;
  features: { googleAuth: boolean; devMailbox: boolean };
};

export type PropertyDetail = PropertyDto & {
  media: MediaDto[];
  reviews: {
    id: string;
    quote: string;
    author: string;
    reviewedAt: string;
    avatarUrl: string | null;
    quotable: boolean;
  }[];
  ideas: {
    id: string;
    title: string;
    why: string;
    format: PostDto['format'];
    brief: string;
    scene: PostDto['scene'];
  }[];
};

export const keys = {
  me: ['me'] as const,
  property: (id: string) => ['property', id] as const,
  media: (id: string, source?: string) => ['media', id, source ?? 'all'] as const,
  posts: (id: string) => ['posts', id] as const,
  plan: (id: string) => ['plan', id] as const,
  post: (id: string) => ['post', id] as const,
  analytics: (id: string) => ['analytics', id] as const,
  home: (id: string, period: number) => ['home', id, period] as const,
  results: (postId: string) => ['results', postId] as const,
  importJob: (id: string) => ['import', id] as const,
  references: (id: string, brief: string, terms: string[] = []) =>
    ['references', id, brief, terms.join(',')] as const,
  emails: ['dev-emails'] as const,
  channels: (id: string) => ['channels', id] as const,
  invoices: ['invoices'] as const,
  team: (id: string) => ['team', id] as const,
  invite: (token: string) => ['invite', token] as const,
};

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => unwrap<Me>(api.api.me.$get()),
    staleTime: 30_000,
    // Credits may change in another tab while this one is hidden.
    refetchOnWindowFocus: 'always',
  });
}

/** Saves an email preference at once, showing the new value before the server answers. */
export function useUpdateNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (json: NotificationsInput) =>
      unwrap<{ notifications: NotificationsDto }>(api.api.me.notifications.$patch({ json })),
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: keys.me });
      const before = qc.getQueryData<Me>(keys.me);
      qc.setQueryData<Me>(keys.me, (old) =>
        old?.notifications ? { ...old, notifications: { ...old.notifications, ...next } } : old,
      );
      return { before };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.before) qc.setQueryData(keys.me, ctx.before);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
}

export function useProperty(id: string | null | undefined) {
  return useQuery({
    queryKey: keys.property(id ?? ''),
    queryFn: () =>
      unwrap<PropertyDetail>(api.api.onboarding.properties[':id'].$get({ param: { id: id! } })),
    enabled: !!id,
  });
}

const KEEPALIVE_MAX = 60_000;

export function useUpdateProperty(id: string) {
  const qc = useQueryClient();
  return useMutation({
    // keepalive: a save fired as the host closes the drawer or reloads still reaches the server
    // instead of being aborted with the page. Browsers cap keepalive bodies at 64 KB in flight,
    // so an unusually large body goes as an ordinary request.
    mutationFn: (json: UpdatePropertyInput) =>
      unwrap<PropertyDto>(
        api.api.onboarding.properties[':id'].$patch(
          { param: { id }, json },
          {
            init: {
              credentials: 'include',
              keepalive: new TextEncoder().encode(JSON.stringify(json)).length < KEEPALIVE_MAX,
            },
          },
        ),
      ),
    onSuccess: (data) => {
      qc.setQueryData<PropertyDetail>(keys.property(id), (old) =>
        old ? { ...old, ...data } : old,
      );
      // Media order can change (hero selection), so refetch the detail as well.
      void qc.invalidateQueries({ queryKey: keys.property(id) });
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useImport(id: string | undefined, poll = true) {
  return useQuery({
    queryKey: keys.importJob(id ?? ''),
    queryFn: () =>
      unwrap<ImportProgress>(api.api.onboarding.imports[':id'].$get({ param: { id: id! } })),
    enabled: !!id,
    refetchInterval: (q) => (poll && !q.state.data?.done ? 450 : false),
    refetchIntervalInBackground: true,
  });
}

export function useMedia(propertyId: string, source?: MediaSource) {
  return useQuery({
    queryKey: keys.media(propertyId, source),
    queryFn: () =>
      unwrap<{ media: MediaDto[] }>(
        api.api.properties[':id'].media.$get({
          param: { id: propertyId },
          query: source ? { source } : {},
        }),
      ).then((r) => r.media),
  });
}

export function usePosts(propertyId: string) {
  return useQuery({
    queryKey: keys.posts(propertyId),
    queryFn: () =>
      unwrap<{ posts: PostDto[] }>(
        api.api.properties[':id'].posts.$get({ param: { id: propertyId }, query: {} }),
      ).then((r) => r.posts),
    // Keep the list fresh while a post is going out to its channels.
    refetchInterval: (q) => (q.state.data?.some((p) => isPublishing(p)) ? 4000 : false),
    refetchIntervalInBackground: false,
  });
}

/** Archived posts only (kept, never deleted); under the posts key so every change refreshes it. */
export function useArchivedPosts(propertyId: string) {
  return useQuery({
    queryKey: [...keys.posts(propertyId), 'archived'] as const,
    queryFn: () =>
      unwrap<{ posts: PostDto[] }>(
        api.api.properties[':id'].posts.$get({
          param: { id: propertyId },
          query: { archived: '1' },
        }),
      ).then((r) => r.posts),
  });
}

/** Archive or restore one post, then refresh every list and number it shows up in. */
function usePostArchiving(propertyId: string, run: (postId: string) => Promise<PostDto>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (post) => {
      qc.setQueryData(keys.post(post.id), post);
      void qc.invalidateQueries({ queryKey: keys.posts(propertyId) });
      void qc.invalidateQueries({ queryKey: keys.plan(propertyId) });
      void qc.invalidateQueries({ queryKey: ['home', propertyId] });
      void qc.invalidateQueries({ queryKey: keys.analytics(propertyId) });
    },
  });
}

export function useArchivePost(propertyId: string) {
  return usePostArchiving(propertyId, postsApi.archive);
}

export function useRestorePost(propertyId: string) {
  return usePostArchiving(propertyId, postsApi.restore);
}

/** The month target: planned of target in the coming window, the next gaps, the wallet line. */
export function usePlan(propertyId: string) {
  return useQuery({
    queryKey: keys.plan(propertyId),
    queryFn: () =>
      unwrap<{ plan: CalendarPlanDto }>(
        api.api.properties[':id'].plan.$get({ param: { id: propertyId } }),
      ).then((r) => r.plan),
    staleTime: 30_000,
  });
}

export function fetchPost(postId: string) {
  return unwrap<{ post: PostDto }>(api.api.posts[':id'].$get({ param: { id: postId } })).then(
    (r) => r.post,
  );
}

export function usePost(postId: string | null) {
  return useQuery({
    queryKey: keys.post(postId ?? ''),
    queryFn: () => fetchPost(postId!),
    enabled: !!postId,
  });
}

export function useAnalytics(propertyId: string) {
  return useQuery({
    queryKey: keys.analytics(propertyId),
    queryFn: () =>
      unwrap<AnalyticsDto>(api.api.properties[':id'].analytics.$get({ param: { id: propertyId } })),
  });
}

/** The browser's zone: Home counts days on the host's clock. */
export function browserTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/** Home: the three tiles, the leaderboard and the usual line for a period; the old data stays while a new period loads. */
export function useHome(propertyId: string, period: HomePeriod) {
  const tz = browserTimezone();
  return useQuery({
    queryKey: keys.home(propertyId, period),
    queryFn: () =>
      unwrap<HomeDto>(
        api.api.properties[':id'].home.$get({
          param: { id: propertyId },
          query: { period: String(period) as '7' | '30' | '90', ...(tz ? { tz } : {}) },
        }),
      ),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function usePostResults(postId: string) {
  const tz = browserTimezone();
  return useQuery({
    queryKey: keys.results(postId),
    queryFn: () =>
      unwrap<PostResultsDto>(
        api.api.posts[':id'].results.$get({ param: { id: postId }, query: tz ? { tz } : {} }),
      ),
    staleTime: 60_000,
  });
}

/**
 * The references the generator would start from for a brief, fetched once for an action.
 * `terms` are a template's subject terms (what carried the post it is modelled on); photos
 * matching them lead. Sent only when there are any, so the plain call is unchanged.
 */
export function fetchReferences(propertyId: string, brief: string, terms: string[] = []) {
  return unwrap<{ references: ReferenceDto[] }>(
    api.api.properties[':id'].references.$get({
      param: { id: propertyId },
      query: { brief, ...(terms.length ? { terms: terms.join(',') } : {}) },
    }),
  ).then((r) => r.references);
}

export function useReferences(
  propertyId: string,
  brief: string,
  enabled: boolean,
  terms: string[] = [],
) {
  return useQuery({
    queryKey: keys.references(propertyId, brief, terms),
    queryFn: () => fetchReferences(propertyId, brief, terms),
    enabled,
    staleTime: 60_000,
  });
}

export const postsApi = {
  create: (propertyId: string, json: CreatePostInput) =>
    unwrap<{ post: PostDto }>(
      api.api.properties[':id'].posts.$post({ param: { id: propertyId }, json }),
    ).then((r) => r.post),
  generate: async (
    postId: string,
    note = '',
    target?: GenerationTarget,
    options?: {
      operation?: GenerationOperation;
      targetCardIds?: string[];
      hostVideoCopy?: HostVideoCopy;
    },
  ) => {
    const result = await unwrap<{ generation: GenerationDto } | { plan: GenerationPlan }>(
      api.api.posts[':id'].generate.$post({
        param: { id: postId },
        json: { note, structured: true, ...(target ? { target } : {}), ...options },
      }),
    );
    if ('plan' in result)
      throw new ApiError(
        422,
        'generation_needs_clarification',
        result.plan.reasons.join(' ') || 'Please describe which part you want to change.',
        { plan: result.plan },
      );
    return result.generation;
  },
  previewGeneration: (postId: string, note: string) =>
    unwrap<{
      preview: { plan: GenerationPlan; plannedStages: string[]; maxProviderCalls: number };
    }>(
      api.api.posts[':id'].generate.$post({
        param: { id: postId },
        json: { note, structured: true, dryRun: true },
      }),
    ).then((r) => r.preview),
  decideGeneration: (postId: string, versionId: string, acceptance: 'accepted' | 'rejected') =>
    unwrap<{ post: PostDto }>(
      fetch(
        `/api/posts/${encodeURIComponent(postId)}/versions/${encodeURIComponent(versionId)}/acceptance`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ acceptance }),
        },
      ),
    ).then((r) => r.post),
  poll: (jobId: string) =>
    unwrap<{ generation: GenerationDto }>(
      api.api.generations[':id'].$get({ param: { id: jobId } }),
    ).then((r) => r.generation),
  update: (
    postId: string,
    json: {
      brief?: string;
      referenceIds?: string[];
      pinnedIds?: string[];
      caption?: string;
      link?: PostLinkInput;
      currentVersionId?: string;
      format?: PostDto['format'];
      mediaMode?: PostDto['mediaMode'];
      channels?: PostDto['channels'];
      reelChoices?: PostDto['reelChoices'];
    },
  ) =>
    unwrap<{ post: PostDto }>(api.api.posts[':id'].$patch({ param: { id: postId }, json })).then(
      (r) => r.post,
    ),
  /** Scheduling to a channel the words were not written for also returns the rewrite to follow. */
  schedule: (postId: string, json: SchedulePostInput) =>
    unwrap<{ post: PostDto; generation: GenerationDto | null }>(
      api.api.posts[':id'].schedule.$post({ param: { id: postId }, json }),
    ),
  /** Off the calendar and back to a draft; nothing goes out. */
  unschedule: (postId: string) =>
    unwrap<{ post: PostDto }>(
      api.api.posts[':id'].unschedule.$post({ param: { id: postId } }),
    ).then((r) => r.post),
  /** Hidden from every list, kept in the database; a scheduled post comes off the calendar. */
  archive: (postId: string) =>
    unwrap<{ post: PostDto }>(api.api.posts[':id'].archive.$post({ param: { id: postId } })).then(
      (r) => r.post,
    ),
  restore: (postId: string) =>
    unwrap<{ post: PostDto }>(api.api.posts[':id'].restore.$post({ param: { id: postId } })).then(
      (r) => r.post,
    ),
  skipIdea: (ideaId: string) =>
    unwrap<{ ok: boolean }>(api.api.ideas[':id'].skip.$post({ param: { id: ideaId } })),
};

export const teamApi = {
  invite: (propertyId: string, email: string) =>
    unwrap<TeamDto>(
      api.api.properties[':id'].team.invites.$post({ param: { id: propertyId }, json: { email } }),
    ),
  revokeInvite: (propertyId: string, inviteId: string) =>
    unwrap<TeamDto>(
      api.api.properties[':id'].team.invites[':inviteId'].$delete({
        param: { id: propertyId, inviteId },
      }),
    ),
  removeMember: (propertyId: string, userId: string) =>
    unwrap<TeamDto>(
      api.api.properties[':id'].team[':userId'].$delete({ param: { id: propertyId, userId } }),
    ),
};

export function useTeam(propertyId: string) {
  return useQuery({
    queryKey: keys.team(propertyId),
    queryFn: () =>
      unwrap<TeamDto>(api.api.properties[':id'].team.$get({ param: { id: propertyId } })),
  });
}

export const invitesApi = {
  accept: (token: string) =>
    unwrap<{ property: PropertyDto }>(
      api.api.invites[':token'].accept.$post({ param: { token } }),
    ).then((r) => r.property),
};

export function useInvite(token: string) {
  return useQuery({
    queryKey: keys.invite(token),
    queryFn: () =>
      unwrap<{ invite: InviteDto }>(api.api.invites[':token'].$get({ param: { token } })).then(
        (r) => r.invite,
      ),
    retry: false,
  });
}

export const billingApi = {
  /** Preview and local only (the route is off in production): pack credits for testing. */
  addTestCredits: (credits = 100) =>
    unwrap<{ credits: CreditsDto }>(api.api.dev.credits.$post({ json: { credits } })),
  setupIntent: () => unwrap<{ clientSecret: string }>(api.api.billing['setup-intent'].$post()),
  /** Puts a property on a tier of the plan (prorated today); takes it off (at renewal). */
  attachPlan: (propertyId: string, plan: 'starter' | 'pro') =>
    unwrap<{ property: PropertyDto; subscription: SubscriptionSummary }>(
      api.api.properties[':id'].plan.$post({ param: { id: propertyId }, json: { plan } }),
    ),
  detachPlan: (propertyId: string) =>
    unwrap<{ property: PropertyDto; subscription: SubscriptionSummary }>(
      api.api.properties[':id'].plan.$delete({ param: { id: propertyId } }),
    ),
  /** Ends the trial today: the interval is charged less the pack credit and the plan's credits land. */
  startPlanNow: () =>
    unwrap<{ subscription: SubscriptionDto; credits: CreditsDto }>(
      api.api.billing['start-plan-now'].$post(),
    ),
  /** Buys whole packs of credits on the card on file. */
  buyPack: (packs: number) =>
    unwrap<{ credits: CreditsDto }>(api.api.billing.credits.pack.$post({ json: { packs } })).then(
      (r) => r.credits,
    ),
  subscribe: (json: {
    plan: 'starter' | 'pro';
    paymentMethodId: string;
    interval?: 'month' | 'year';
  }) =>
    unwrap<{ subscription: SubscriptionDto }>(api.api.billing.subscribe.$post({ json })).then(
      (r) => r.subscription,
    ),
  changePlan: (plan: 'starter' | 'pro', interval?: 'month' | 'year') =>
    unwrap<{ subscription: SubscriptionDto }>(
      api.api.billing['change-plan'].$post({ json: { plan, interval } }),
    ).then((r) => r.subscription),
  cancel: () =>
    unwrap<{ subscription: SubscriptionDto }>(api.api.billing.cancel.$post()).then(
      (r) => r.subscription,
    ),
  resume: () =>
    unwrap<{ subscription: SubscriptionDto }>(api.api.billing.resume.$post()).then(
      (r) => r.subscription,
    ),
  updatePaymentMethod: (paymentMethodId: string) =>
    unwrap<{ subscription: SubscriptionDto }>(
      api.api.billing['payment-method'].$post({ json: { paymentMethodId } }),
    ).then((r) => r.subscription),
};

export type InvoiceDto = {
  id: string;
  number: string | null;
  createdAt: string;
  amountCents: number;
  currency: string;
  status: 'paid' | 'open' | 'void' | 'uncollectible' | 'draft';
  description: string | null;
  hostedUrl: string | null;
  pdfUrl: string | null;
};

export function useInvoices(enabled: boolean) {
  return useQuery({
    queryKey: keys.invoices,
    queryFn: () =>
      unwrap<{ invoices: InvoiceDto[] }>(api.api.billing.invoices.$get()).then((r) => r.invoices),
    enabled,
    staleTime: 60_000,
  });
}

export const onboardingApi = {
  // The host's zone rides along: the property's clock until the listing's location says better.
  startImport: (url: string, loggedIn: boolean) =>
    loggedIn
      ? unwrap<{ importId: string; propertyId: string }>(
          api.api.properties.$post({ json: { url, timezone: hostTimeZone() } }),
        )
      : unwrap<{ importId: string; propertyId: string }>(
          api.api.onboarding.imports.$post({ json: { url, timezone: hostTimeZone() } }),
        ),
  claim: (propertyId: string) =>
    unwrap<{ property: PropertyDto }>(api.api.onboarding.claim.$post({ json: { propertyId } })),
};

export const mediaApi = {
  addLink: (propertyId: string, url: string) =>
    unwrap<{ media: MediaDto }>(
      api.api.properties[':id'].media.link.$post({ param: { id: propertyId }, json: { url } }),
    ),
  remove: (id: string) => unwrap<{ ok: boolean }>(api.api.media[':id'].$delete({ param: { id } })),
  rename: (id: string, label: string) =>
    unwrap<{ media: MediaDto }>(api.api.media[':id'].$patch({ param: { id }, json: { label } })),
};

export function useDevEmails(enabled: boolean) {
  return useQuery({
    queryKey: keys.emails,
    queryFn: () =>
      unwrap<{
        emails: {
          id: string;
          to: string;
          subject: string;
          html: string;
          text: string;
          createdAt: string;
        }[];
      }>(api.api.dev.emails.$get()).then((r) => r.emails),
    enabled,
    refetchInterval: 3000,
    refetchIntervalInBackground: true,
  });
}

/** What no signed-out page may sell: the channels still in the platforms' review. */
export function useChannelsInReview(): Channel[] {
  const config = useQuery({
    queryKey: ['billing-config'] as const,
    queryFn: () => unwrap<{ channelsInReview: Channel[] }>(api.api.billing.config.$get()),
    staleTime: 5 * 60_000,
  });
  return config.data?.channelsInReview ?? [];
}

export type ChannelsData = { connections: ConnectionDto[]; providers: ProviderAvailability[] };

export function useChannels(propertyId: string | null | undefined) {
  return useQuery({
    queryKey: keys.channels(propertyId ?? ''),
    queryFn: () =>
      unwrap<ChannelsData>(api.api.properties[':id'].channels.$get({ param: { id: propertyId! } })),
    enabled: !!propertyId,
    staleTime: 15_000,
  });
}

export type TikTokCreator = {
  nickname: string | null;
  username: string | null;
  avatarUrl: string | null;
  privacyOptions: string[];
  commentDisabled: boolean;
  duetDisabled: boolean;
  stitchDisabled: boolean;
  maxVideoSeconds: number | null;
};

/** What TikTok lets this creator choose; the host confirms it before a direct post. */
export function useTikTokCreator(connectionId: string | null | undefined) {
  return useQuery({
    queryKey: ['tiktok-creator', connectionId ?? ''] as const,
    queryFn: () =>
      unwrap<{ creator: TikTokCreator }>(
        api.api.channels[':id'].tiktok['creator-info'].$get({ param: { id: connectionId! } }),
      ).then((r) => r.creator),
    enabled: !!connectionId,
    staleTime: 60_000,
    retry: false,
  });
}

export const channelsApi = {
  connect: (propertyId: string, provider: ProviderKey) =>
    unwrap<{ url: string; state: string }>(
      api.api.properties[':id'].channels[':provider'].connect.$post({
        param: { id: propertyId, provider },
      }),
    ),
  pending: (state: string) =>
    unwrap<{
      provider: ProviderKey;
      label: string;
      propertyId: string;
      accounts: {
        externalId: string;
        displayName: string;
        handle: string | null;
        avatarUrl: string | null;
      }[];
    }>(api.api.channels.pending[':state'].$get({ param: { state } })),
  select: (state: string, externalId: string) =>
    unwrap<{ connection: ConnectionDto }>(
      api.api.channels.pending[':state'].select.$post({ param: { state }, json: { externalId } }),
    ).then((r) => r.connection),
  disconnect: (id: string) =>
    unwrap<{ ok: boolean }>(api.api.channels[':id'].$delete({ param: { id } })),
};

/** Bookings the host attributes to social by hand; the only booking source. */
export const bookingsApi = {
  log: (propertyId: string, json: LogBookingInput) =>
    unwrap<{ booking: BookingDto }>(
      api.api.properties[':id'].bookings.$post({ param: { id: propertyId }, json }),
    ).then((r) => r.booking),
  remove: (bookingId: string) =>
    unwrap<{ ok: boolean }>(api.api.bookings[':id'].$delete({ param: { id: bookingId } })),
};

export const devApi = {
  runPublisher: () => unwrap<PublisherRun>(api.api.dev['publish-due'].$post()),
  refreshPlans: () => unwrap<{ refreshed: string[] }>(api.api.dev['refresh-plans'].$post()),
};

/** True while a post is being pushed to its channels right now (queued & due, publishing, pending). */
export function isPublishing(p: PostDto, now = Date.now()): boolean {
  if (p.status !== 'scheduled' || p.imported) return false;
  const due = !p.scheduledAt || new Date(p.scheduledAt).getTime() <= now;
  return p.publishJobs.some(
    (j) =>
      j.status === 'publishing' ||
      j.status === 'pending' ||
      (j.status === 'queued' &&
        due &&
        (!j.nextAttemptAt || new Date(j.nextAttemptAt).getTime() <= now)),
  );
}

export const accountApi = {
  /** Deletes the account now; `confirm` is the email the host typed. */
  delete: (confirm: string) => unwrap<{ ok: true }>(api.api.me.$delete({ json: { confirm } })),
};

/** Where a Meta data deletion request stands; disabled without a code. */
export function useDataDeletionStatus(code: string | null) {
  return useQuery({
    queryKey: ['data-deletion', code] as const,
    queryFn: () =>
      unwrap<{ status: string; requestedAt: string }>(
        api.api.channels.meta['data-deletion'][':code'].$get({ param: { code: code! } }),
      ),
    enabled: !!code,
    retry: false,
  });
}
